import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useMemo, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  PackageOpen, Search, ArrowDownCircle, ArrowUpCircle, Loader2, History,
  AlertTriangle, Plus, Minus
} from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch { return ''; }
};

export default function HesapStockPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogFor, setDialogFor] = useState(null); // { product, type }
  const [form, setForm] = useState({ quantity: '', unit_price: '', supplier: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('urunler'); // 'urunler' | 'hareketler'

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const loadAll = async () => {
    try {
      const [pRes, mRes, sRes] = await Promise.all([
        fetch(`${API}/products`, { headers }),
        fetch(`${API}/stock-movements?limit=300`, { headers }),
        fetch(`${API}/products/suggestions`, { headers }),
      ]);
      if (pRes.ok) setProducts(await pRes.json());
      if (mRes.ok) setMovements(await mRes.json());
      if (sRes.ok) setSuppliers((await sRes.json()).suppliers || []);
    } finally { setLoading(false); }
  };

  const openDialog = (product, type) => {
    setDialogFor({ product, type });
    setForm({ quantity: '', unit_price: type === 'in' ? (product.buy_price || '') : '', supplier: product.supplier || '', note: '' });
  };

  const submitMovement = async (e) => {
    e.preventDefault();
    const qty = parseInt(form.quantity) || 0;
    if (qty <= 0) { toast.error('Miktar 0\'dan büyük olmalı'); return; }
    setSaving(true);
    try {
      const body = {
        product_id: dialogFor.product.id,
        type: dialogFor.type,
        quantity: qty,
        unit_price: parseFloat(form.unit_price) || 0,
        supplier: form.supplier || '',
        note: form.note || '',
      };
      const r = await fetch(`${API}/stock-movements`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!r.ok) throw new Error();
      toast.success(dialogFor.type === 'in' ? `Stok girişi yapıldı: +${qty}` : `Stok çıkışı yapıldı: -${qty}`);
      setDialogFor(null);
      loadAll();
    } catch { toast.error('İşlem başarısız'); }
    finally { setSaving(false); }
  };

  const filteredProducts = useMemo(() => products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.barcode || '').includes(search) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.brand || '').toLowerCase().includes(search.toLowerCase())
  ), [products, search]);

  const filteredMovements = useMemo(() => {
    if (!search) return movements;
    const q = search.toLowerCase();
    return movements.filter(m =>
      (m.product_name || '').toLowerCase().includes(q) ||
      (m.supplier || '').toLowerCase().includes(q) ||
      (m.note || '').toLowerCase().includes(q)
    );
  }, [movements, search]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="hesap-stock-page">
      <div className="flex items-center gap-3">
        <PackageOpen className="h-7 w-7 text-indigo-600" />
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Stok Giriş / Çıkış</h1>
      </div>

      {/* Tab switch */}
      <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
        <button
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${tab === 'urunler' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-slate-600 dark:text-slate-400'}`}
          onClick={() => setTab('urunler')}
          data-testid="tab-products"
        >
          <PackageOpen className="h-4 w-4 inline mr-1" />Ürünler ({products.length})
        </button>
        <button
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${tab === 'hareketler' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-slate-600 dark:text-slate-400'}`}
          onClick={() => setTab('hareketler')}
          data-testid="tab-history"
        >
          <History className="h-4 w-4 inline mr-1" />Hareket Geçmişi ({movements.length})
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={tab === 'urunler' ? 'Ürün, barkod, kategori ara...' : 'Ürün adı, tedarikçi, not ara...'}
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {tab === 'urunler' ? (
        filteredProducts.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <PackageOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">{search ? 'Sonuç bulunamadı' : 'Henüz ürün yok — önce Ürünler sayfasından ürün ekleyin'}</p>
          </CardContent></Card>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Ürün</th>
                  <th className="text-left p-3 font-medium text-slate-600">Barkod</th>
                  <th className="text-left p-3 font-medium text-slate-600">Kategori</th>
                  <th className="text-right p-3 font-medium text-slate-600">Mevcut Stok</th>
                  <th className="text-right p-3 font-medium text-slate-600">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(p => {
                  const lowStock = p.stock <= p.min_stock;
                  return (
                    <tr key={p.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                      <td className="p-3 font-medium">
                        <div className="flex items-center gap-2">
                          {p.image_url && <img src={p.image_url} alt="" className="h-8 w-8 object-cover rounded border" loading="lazy" />}
                          <div>
                            <div>{p.name}</div>
                            {p.brand && <div className="text-[10px] text-slate-400">{p.brand}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-slate-500 text-xs">{p.barcode || '-'}</td>
                      <td className="p-3"><span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-xs">{p.category || '-'}</span></td>
                      <td className="p-3 text-right">
                        <span className={`inline-flex items-center gap-1 font-semibold ${lowStock ? 'text-red-600' : 'text-slate-700 dark:text-slate-200'}`}>
                          {lowStock && <AlertTriangle className="h-3.5 w-3.5" />}
                          {p.stock} {p.unit}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 h-8"
                            onClick={() => openDialog(p, 'in')}
                            data-testid={`stock-in-${p.id}`}
                          >
                            <Plus className="h-3.5 w-3.5 mr-1" />Giriş
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-300 text-rose-600 hover:bg-rose-50 h-8"
                            onClick={() => openDialog(p, 'out')}
                            data-testid={`stock-out-${p.id}`}
                          >
                            <Minus className="h-3.5 w-3.5 mr-1" />Çıkış
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : (
        filteredMovements.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <History className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">Henüz stok hareketi yok</p>
          </CardContent></Card>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Tarih</th>
                  <th className="text-left p-3 font-medium text-slate-600">Ürün</th>
                  <th className="text-left p-3 font-medium text-slate-600">Tür</th>
                  <th className="text-right p-3 font-medium text-slate-600">Miktar</th>
                  <th className="text-right p-3 font-medium text-slate-600">Birim Fiyat</th>
                  <th className="text-left p-3 font-medium text-slate-600">Tedarikçi</th>
                  <th className="text-left p-3 font-medium text-slate-600">Not</th>
                  <th className="text-right p-3 font-medium text-slate-600">Sonraki Stok</th>
                </tr>
              </thead>
              <tbody>
                {filteredMovements.map(m => (
                  <tr key={m.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                    <td className="p-3 text-xs text-slate-500">{formatDate(m.created_at)}</td>
                    <td className="p-3 font-medium">{m.product_name}</td>
                    <td className="p-3">
                      {m.type === 'in' ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">
                          <ArrowDownCircle className="h-3.5 w-3.5" />GİRİŞ
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-semibold">
                          <ArrowUpCircle className="h-3.5 w-3.5" />ÇIKIŞ
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right font-medium">
                      <span className={m.type === 'in' ? 'text-emerald-600' : 'text-rose-600'}>
                        {m.type === 'in' ? '+' : '-'}{m.quantity}
                      </span>
                    </td>
                    <td className="p-3 text-right text-slate-600">{m.unit_price ? `${m.unit_price.toLocaleString('tr-TR')} TL` : '-'}</td>
                    <td className="p-3 text-slate-500 text-xs">{m.supplier || '-'}</td>
                    <td className="p-3 text-slate-500 text-xs">{m.note || '-'}</td>
                    <td className="p-3 text-right text-slate-700">{m.stock_after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <datalist id="stock-supplier-list">
        {suppliers.map(s => <option key={s} value={s} />)}
      </datalist>

      <Dialog open={!!dialogFor} onOpenChange={(o) => !o && setDialogFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dialogFor?.type === 'in' ? (
                <><ArrowDownCircle className="h-5 w-5 text-emerald-600" />Stok Girişi</>
              ) : (
                <><ArrowUpCircle className="h-5 w-5 text-rose-600" />Stok Çıkışı</>
              )}
            </DialogTitle>
          </DialogHeader>
          {dialogFor && (
            <form onSubmit={submitMovement} className="space-y-3">
              <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded">
                <p className="font-semibold">{dialogFor.product.name}</p>
                <p className="text-xs text-slate-500">
                  Mevcut stok: <span className="font-medium text-slate-700 dark:text-slate-200">{dialogFor.product.stock} {dialogFor.product.unit}</span>
                </p>
              </div>
              <div className="space-y-1">
                <Label>Miktar *</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.quantity}
                  onChange={(e) => setForm({...form, quantity: e.target.value})}
                  autoFocus
                  data-testid="stock-quantity-input"
                />
                {form.quantity && dialogFor.type === 'out' && parseInt(form.quantity) > dialogFor.product.stock && (
                  <p className="text-xs text-amber-600">⚠️ Miktar mevcut stoktan fazla — stok negatif olacak</p>
                )}
              </div>
              {dialogFor.type === 'in' && (
                <>
                  <div className="space-y-1">
                    <Label>Birim Alış Fiyatı (TL)</Label>
                    <Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({...form, unit_price: e.target.value})} />
                  </div>
                  <div className="space-y-1">
                    <Label>Tedarikçi</Label>
                    <Input
                      value={form.supplier}
                      onChange={(e) => setForm({...form, supplier: e.target.value})}
                      list="stock-supplier-list"
                      placeholder="Kimden alındı?"
                    />
                  </div>
                </>
              )}
              <div className="space-y-1">
                <Label>Not</Label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm({...form, note: e.target.value})}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px]"
                  placeholder={dialogFor.type === 'in' ? 'Ör: Fatura no, açıklama...' : 'Ör: Hasar, iade, fire...'}
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setDialogFor(null)} className="flex-1">İptal</Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className={`flex-1 ${dialogFor.type === 'in' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
                  data-testid="stock-submit-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (dialogFor.type === 'in' ? 'Girişi Onayla' : 'Çıkışı Onayla')}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
